import { useMutation, UseMutationResult } from "@tanstack/react-query"
import { auth } from "../lib/firebase"

interface BatchDeleteParams {
	caseId: string | undefined
	documentIds: string[]
}

const batchDeleteDocuments = async ({
	caseId,
	documentIds,
}: BatchDeleteParams): Promise<void> => {
	if (!caseId) {
		throw new Error("Case ID is required for batch deletion.")
	}
	if (documentIds.length === 0) {
		// No need to call API if no documents are selected
		return
	}

	const backendUrl = import.meta.env.VITE_API_URL
	if (!backendUrl) {
		throw new Error("VITE_API_URL environment variable not set.")
	}

	const user = auth.currentUser

	if (!user) {
		throw new Error("User not found.")
	}

	const token = await user.getIdToken()

	const response = await fetch(
		`${backendUrl}/api/cases/${caseId}/documentsbatch/`,
		{
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ document_ids: documentIds }),
		}
	)

	if (!response.ok) {
		// Check for non-204 specifically, or just non-ok
		const errorText = await response.text()
		throw new Error(
			`Failed to batch delete documents: ${response.status} ${errorText}`
		)
	}

	// Expecting 204 No Content on success
	return
}

export const useBatchDeleteDocuments = (): UseMutationResult<
	void, // Return type on success
	Error, // Type of error
	BatchDeleteParams // Type of variables passed to mutate function
> => {
	return useMutation<void, Error, BatchDeleteParams>({
		mutationFn: batchDeleteDocuments,
		// Optional: Add callbacks like onSuccess, onError, onSettled
		onError: (error) => {
			console.error("Error during batch delete:", error.message)
		},
	})
}
